import { useMutation, useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import useLinkProps from '../../../../hooks/use-link-props';
import {
  createPartAlertForAuthUser,
  createPartAlertForNoAuthUser,
} from '../../../../services/private-area/part-alerts';
import { useAddAlert } from '../../alerts';

import AlertFormContent from './form-content';
import useLoggedUser from '../../../../hooks/use-logged-user';

const AlertForm = ({ vehicle, info }) => {
  const { t } = useTranslation();
  const {
    locale = 'en',
    push,
    asPath,
    query: { subCategory },
  } = useRouter();
  const client = useQueryClient();
  const getLink = useLinkProps();
  const addAlert = useAddAlert();
  const user = useLoggedUser();

  const method = useForm({
    mode: 'onChange',
    defaultValues: {
      schedule: '15d',
      part: info?.partSlug || subCategory,
    },
  });

  const {
    handleSubmit,
    setError,
    formState: { errors, isValid },
  } = method

  const { mutate, isLoading } = user
    ? useMutation(createPartAlertForAuthUser, {
      onSuccess() {
        client.removeQueries('alerts');
        addAlert({
          id: 'create-alert',
          text: t('private-area:part-alerts-create.form.success'),
          type: 'SUCCESS',
          autoClose: true,
        });
      },
      onError(error: Record<string, any>) {
        if (error.notAutorized) {
          const link = getLink('login', {
            redirect: encodeURIComponent(`/${locale}/${asPath}`),
          });
          push(link.href, link.as);
        } else {
          let message;
          if (error.serverError) {
            message = t('forms:errors.serverError');
          } else {
            message = t('private-area:part-alerts-create.form.error');
          }
          addAlert({
            id: 'create-alert',
            text: message,
            items: error.non_field_errors,
            type: 'DANGER',
          });
          Object.keys(error).map(function (key, i) {
            if (key !== 'non_field_errors' && key !== 'serverError') {
              setError(key, {
                type: 'manual',
                message: error[key],
              });
            }
          });
        }
      },
    })
    : useMutation(createPartAlertForNoAuthUser, {
      onSuccess() {
        client.removeQueries('alerts');
        addAlert({
          id: 'create-alert',
          text: t('private-area:part-alerts-create.form.success'),
          type: 'SUCCESS',
          autoClose: true,
        });
        const link = getLink('private-area-part-alerts', undefined);
        push(link.href, link.as);
      },
      onError(error: Record<string, any>) {
        let message;
        if (error.serverError) {
          message = t('forms:errors.serverError');
        } else {
          message = t('private-area:part-alerts-create.form.error');
        }
        addAlert({
          id: 'create-alert',
          text: message,
          items: error.non_field_errors,
          type: 'DANGER',
        });
        Object.keys(error).map(function (key, i) {
          if (key !== 'non_field_errors' && key !== 'serverError') {
            setError(key, {
              type: 'manual',
              message: error[key],
            });
          }
        });
      },
    });

  const onSubmit = useCallback(
    (formData) => {
      if (formData.version) {
        formData.car_identifier = formData.version;
      } else {
        formData.car_identifier = formData.model;
      }
      const newUser = user
        ? undefined
        : {
          name: formData.name,
          email: formData.email,
          language: locale,
          shipping_destination: Cookies.get('_shipping_location'),
          currency: Cookies.get('_currency'),
        };

      mutate({
        locale: locale,
        formData: {
          car_identifier: formData.car_identifier,
          part_identifier: formData.part,
          reference: formData.reference,
          schedule: formData.schedule,
          ...newUser,
        },
        csrftoken: Cookies.get('csrftoken') || '',
      });
    },
    [mutate, locale]
  );

  return (
    <>
      <FormProvider {...method}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="pt-6 lg:pt-12 grid gap-6"
        >
          <AlertFormContent
            disabled={isLoading}
            isValid={isValid && Object.keys(errors).length == 0 && !isLoading}
            vehicle={vehicle}
            info={info}
            subCategory={subCategory}
            user={user}
            showPassword={false}
          />
        </form>
      </FormProvider >
    </>
  );
};
export default AlertForm;
